<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useHorizontalScrollInteractions } from '../composables/useHorizontalScrollInteractions';
import { usePostSaleRegistryStatus } from '../composables/usePostSaleRegistryStatus';
import PostSaleRegistryStatus from './PostSaleRegistryStatus.vue';
import PostSaleStatusTimelineItem from './PostSaleStatusTimelineItem.vue';
const props = defineProps({
  caseData: { type: Object, required: true },
});
const emit = defineEmits(['changeStatus', 'openAnalysis', 'select']);
const { t } = useI18n();
const timelineScroll = ref(null);
const currentCaseData = computed(() => props.caseData);
const registryStatus = usePostSaleRegistryStatus({ caseData: currentCaseData });
const { endDrag, isDragging, onPointerDown, onPointerMove, onWheel } =
  useHorizontalScrollInteractions();
const statusLabels = {
  entrada: t('POST_SALE.STATUS.ENTRY'),
  analise: t('POST_SALE.STATUS.ANALYSIS'),
  certidoes: t('POST_SALE.STATUS.CERTIFICATES'),
  taxas: t('POST_SALE.STATUS.TAXES'),
  itbi: t('POST_SALE.STATUS.ITBI'),
  banco_documentacao: t('POST_SALE.STATUS.BANK_DOCUMENTATION'),
  engenharia: t('POST_SALE.STATUS.ENGINEERING'),
  emissao_contrato: t('POST_SALE.STATUS.CONTRACT_ISSUANCE'),
  averbacao: t('POST_SALE.STATUS.ANNOTATION'),
  laudemio: t('POST_SALE.STATUS.LAUDEMIUM'),
  escritura: t('POST_SALE.STATUS.DEED'),
  registro: t('POST_SALE.STATUS.REGISTRY'),
  entrega_chaves: t('POST_SALE.STATUS.KEY_HANDOVER'),
  correcao: t('POST_SALE.STATUS.CORRECTION'),
  finalizado: t('POST_SALE.STATUS.FINISHED'),
  cancelado: t('POST_SALE.STATUS.CANCELED'),
};
const daysElapsed = computed(() => {
  if (props.caseData.days_elapsed !== undefined) {
    return props.caseData.days_elapsed;
  }
  if (!props.caseData.entered_at) return null;
  const elapsed = Date.now() - new Date(props.caseData.entered_at).getTime();
  return Math.max(0, Math.floor(elapsed / 86400000));
});
const deadlineClass = computed(() =>
  props.caseData.deadline?.bucket === 'vermelhos'
    ? 'bg-red-50 text-red-700'
    : 'bg-emerald-50 text-emerald-700'
);
const labelFor = (item, index, events) => {
  if (events[index - 1]?.to_status === 'correcao') {
    return t('POST_SALE.STATUS_DETAILS.CORRECTED_SALE');
  }

  return (
    item.to_status_label ||
    statusLabels[item.to_status] ||
    t('POST_SALE.STATUS.SELECT')
  );
};
const statusItems = computed(() => {
  const events = (props.caseData.events || []).filter(
    event =>
      event.to_status && ['opened', 'status_changed'].includes(event.event_type)
  );
  const source = events.length
    ? events
    : [
        {
          id: `current-${props.caseData.id}`,
          to_status: props.caseData.status,
          to_status_label: props.caseData.status_label,
          occurred_at: props.caseData.entered_at,
          status_due_at: props.caseData.status_due_at,
          payload: props.caseData.status_payload,
        },
      ];
  if (!source.some(event => event.to_status === props.caseData.status)) {
    source.push({
      id: `current-${props.caseData.id}`,
      to_status: props.caseData.status,
      to_status_label: props.caseData.status_label,
      occurred_at: props.caseData.updated_at || props.caseData.entered_at,
      status_due_at: props.caseData.status_due_at,
      payload: props.caseData.status_payload,
    });
  }
  const currentIndex = source.reduce(
    (index, event, eventIndex) =>
      event.to_status === props.caseData.status ? eventIndex : index,
    -1
  );
  return source.map((event, index) => ({
    ...event,
    key: `${event.id || event.to_status}-${index}`,
    label: labelFor(event, index, source),
    isCurrent: index === currentIndex,
    isCompleted: currentIndex >= 0 && index < currentIndex,
  }));
});

const selectStatus = item => {
  if (item.to_status === 'entrada') return;
  if (item.to_status === 'analise') {
    emit('openAnalysis', item);
    return;
  }
  if (item.isCurrent && props.caseData.permissions?.can_manage) {
    emit('changeStatus', item);
    return;
  }

  emit('select', item);
};

const focusLatestStatus = async () => {
  await nextTick();
  const element = timelineScroll.value;
  if (!element || element.scrollWidth <= element.clientWidth) return;

  element.scrollLeft = element.scrollWidth - element.clientWidth;
};

onMounted(focusLatestStatus);
watch(
  () => [
    props.caseData.id,
    props.caseData.status,
    props.caseData.events?.length,
  ],
  focusLatestStatus,
  { flush: 'post' }
);
</script>

<template>
  <LaisCard data-testid="post-sale-status-timeline" density="flush">
    <template #header>
      <div class="flex w-full flex-wrap items-center justify-between gap-3">
        <h2
          class="text-xl font-bold leading-6 text-lais-text font-redHatDisplay"
        >
          {{ t('POST_SALE.STATUS_DETAILS.TIMELINE_TITLE') }}
        </h2>
        <div
          class="flex flex-wrap items-center gap-3 text-xs text-lais-text-muted"
        >
          <span data-testid="post-sale-status-days">
            {{
              t('POST_SALE.STATUS_DETAILS.DAYS_ELAPSED', {
                days: daysElapsed ?? t('POST_SALE.COMMON.EMPTY_VALUE'),
              })
            }}
          </span>
          <span
            v-if="caseData.deadline?.label"
            data-testid="post-sale-status-deadline"
            class="rounded-full px-2 py-1 font-semibold"
            :class="deadlineClass"
          >
            {{ caseData.deadline.label }}
          </span>
        </div>
      </div>
    </template>
    <div v-if="!statusItems.length" class="p-5 text-sm text-lais-text-muted">
      {{ t('POST_SALE.EMPTY.STATUS_LINE') }}
    </div>
    <div
      v-else
      ref="timelineScroll"
      data-testid="post-sale-status-timeline-scroll"
      class="overflow-x-auto whitespace-nowrap"
      :class="isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'"
      @pointercancel="endDrag"
      @pointerdown="onPointerDown"
      @pointerleave="endDrag"
      @pointermove="onPointerMove"
      @pointerup="endDrag"
      @wheel="onWheel"
    >
      <div
        class="flex min-w-max items-center gap-0 bg-lais-primary-50 px-4 py-[50px]"
      >
        <template v-for="(item, index) in statusItems" :key="item.key">
          <PostSaleStatusTimelineItem
            :item="item"
            :show-arrow="index < statusItems.length - 1"
            @select="selectStatus"
          />
        </template>
        <LaisPrimaryButton
          v-if="caseData.permissions?.can_manage"
          data-testid="post-sale-status-change"
          variant="ghost"
          icon="i-lucide-plus"
          class="ml-2 flex h-[172px] w-[200px] shrink-0 flex-col justify-center gap-3 !rounded-lais-sm !p-6 text-center"
          @click="emit('changeStatus')"
        >
          {{ t('POST_SALE.STATUS_DETAILS.CHANGE_STATUS') }}
        </LaisPrimaryButton>
      </div>
    </div>
    <PostSaleRegistryStatus
      v-if="caseData.status === 'registro'"
      :loading="registryStatus.loading.value"
      :result="registryStatus.result.value"
    />
  </LaisCard>
</template>
